import React from 'react';
import {
  Brain,
  Zap,
  ShieldOff,
  Crown,
  Rocket,
  TrendingUp,
  Infinity as InfinityIcon,
} from 'lucide-react';

const PAIN_POINTS = [
  "You work hard — sometimes 12-hour days — but your income keeps hitting the same invisible ceiling.",
  "You've read 20 self-help books and watched 100 manifestation reels. Nothing has actually changed.",
  'Every time you make money, it leaves before you can grow it. Bills, emergencies, impulse purchases.',
  "Deep down, you know you're capable of more — but something inside keeps you playing small.",
  "You see others your age building wealth and you wonder: 'What do they have that I don't?'",
  "You've tried affirmations, vision boards, gratitude journals — they feel good for a week, then fade.",
];

const SHIFTS = [
  {
    icon: Brain,
    title: 'Reprogram Your Mind',
    desc: 'Rewire decades of money conditioning and install beliefs that attract abundance.',
  },
  {
    icon: Zap,
    title: 'Align Your Energy',
    desc: 'Learn to manage your energetic signal so wealth flows toward you, not past you.',
  },
  {
    icon: ShieldOff,
    title: 'Overcome Fear & Doubt',
    desc: 'Dismantle the subconscious saboteurs that stall your decisions and drain your power.',
  },
  {
    icon: Crown,
    title: 'Install a Wealth Identity',
    desc: 'Stop wanting wealth. Start being the person who already has it.',
  },
  {
    icon: Rocket,
    title: 'Take Aligned Action',
    desc: 'Move from scattered effort to inspired, high-leverage action that moves the needle.',
  },
  {
    icon: TrendingUp,
    title: 'Build Multiple Income Streams',
    desc: 'Architect your life for freedom with diversified, compounding sources of income.',
  },
  {
    icon: InfinityIcon,
    title: 'Live in Flow & Abundance',
    desc: 'Hold wealth without leaking it. Grow calmly, steadily, and sustainably.',
  },
];

export default function BookShifts() {
  return (
    <>
      {/* Pain points */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5 text-center">
            Read This Before You Scroll Away
          </p>
          <h2 className="font-serif text-center text-white text-[32px] md:text-[48px] leading-[1.08] tracking-tight">
            Does any of this sound
            <br />
            <span className="italic text-amber-200">painfully familiar?</span>
          </h2>

          <ul className="mt-12 grid md:grid-cols-2 gap-3 md:gap-4">
            {PAIN_POINTS.map((p, i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-navy-900/40 px-5 py-4"
              >
                <span className="shrink-0 mt-0.5 text-amber-300 font-serif text-lg">→</span>
                <p className="text-neutral-300 text-[14px] md:text-[15px] leading-relaxed">{p}</p>
              </li>
            ))}
          </ul>

          <p className="mt-12 text-center text-neutral-400 text-[15px] md:text-[17px] leading-relaxed max-w-2xl mx-auto">
            Then you weren't broken. You were just running the wrong code.
          </p>
          <p className="mt-4 text-center text-neutral-500 text-[14px] md:text-[15px] leading-relaxed max-w-2xl mx-auto">
            The truth nobody tells you: your bank balance is the{' '}
            <em className="text-amber-200/90">output</em> of an inner program running on autopilot
            since childhood. Until you rewrite that program, nothing on the outside will
            permanently change.
          </p>
        </div>
      </section>

      {/* 7 Shifts */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5 text-center">
            Inside The Book · The Seven Shifts
          </p>
          <h2 className="font-serif text-center text-white text-[32px] md:text-[48px] leading-[1.08] tracking-tight">
            Seven specific shifts.
            <br />
            <span className="italic text-amber-200">One new life.</span>
          </h2>
          <p className="mt-5 text-center text-neutral-400 text-[15px] md:text-[16px] leading-relaxed max-w-2xl mx-auto">
            Each chapter installs one of these shifts inside you — with assignments designed to
            make them stick.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SHIFTS.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-navy-900/70 to-navy-950/70 p-6 md:p-7 transition-all duration-300 hover:border-amber-300/30 hover:-translate-y-[2px]"
              >
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-amber-300/[0.04] blur-3xl group-hover:bg-amber-300/[0.1] transition-colors" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-11 h-11 rounded-xl bg-amber-300/10 border border-amber-300/25 flex items-center justify-center text-amber-300">
                      <Icon size={20} strokeWidth={1.5} />
                    </span>
                    <span className="font-serif text-amber-200 text-sm">0{i + 1}</span>
                  </div>
                  <h3 className="font-serif text-white text-[22px] md:text-[24px] tracking-tight">
                    {title}
                  </h3>
                  <p className="mt-3 text-neutral-400 text-[14px] leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
